/**
 * Purpose: Platform detection utilities for web vs. native (Capacitor) builds.
 * Owns: Runtime platform checks, router type selection logic.
 * Notes: Uses Capacitor's built-in isNativePlatform() for runtime detection.
 *        Supports a Vite env variable override (VITE_FORCE_HASH_ROUTER=true)
 *        for testing hash-based routing in web dev mode.
 */

import { Capacitor } from '@capacitor/core';

/**
 * Returns true when the app is running inside a Capacitor native shell
 * (iOS or Android), or when the VITE_FORCE_HASH_ROUTER env flag is set.
 *
 * Why this matters: Native Capacitor apps serve files from the local filesystem
 * (e.g., capacitor://localhost or file://), where HTML5 pushState routing does
 * not work. HashRouter uses URL fragments (#/path) that work everywhere.
 */
export function isNativePlatform(): boolean {
  if (import.meta.env.VITE_FORCE_HASH_ROUTER === 'true') {
    return true;
  }
  return Capacitor.isNativePlatform();
}

/**
 * Returns true when the app is running in a standard web browser (not native).
 */
export function isWebPlatform(): boolean {
  return !isNativePlatform();
}
